import { ReactNode, useContext, useMemo, useState } from "react";
import { LinkButton } from "../../../common/buttons/LinkButton/LinkButton";
import { MoreDropdown } from "../../../common/buttons/MoreDropdown/MoreDropdown";
import { EntityRestoreButton } from "../../../common/entity/entityComponents/EntityRestoreButton";
import { EntityDetailView } from "../../../common/entity/EntityDetailPage";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { NotAllowed } from "../../../common/misc/UIconstants";
import Detail from "../../../common/panels/Detail/Detail";
import { DetailsPageTopControlsView } from "../../../common/panels/misc/DetailsPageTopControlsView/DetailsPageTopControlsView";
import { useEntityPermissions } from "../../../common/permissions/useEntityPermissions";
import { GenericEntity, GenericEntityComponentProps } from "../GenericComponents";
import { GenericEntityDetailTable, GenericEntityDetailTableProps } from "./GenericEntityDetailTable";
import { AlertModal } from "../../../common/modals/AlertModal/AlertModal";
import { genericEntityConstants, GenericEntityConstantsEntities } from "../../../api/GenericConstants";
import { useEntityApi } from "../../../api/useEntityApi";
import { showtoast } from "../../../common/overlays/Toasts/showtoast";
import { toUppercase } from "../../../common/helperfunctions/stringFunctions";
import { useHistory } from "react-router-dom";
import { SessionContext } from "../../../common/contexts/SessionContext";
import { CustomFieldReferencesTile } from "../../../common/sidebar/tiles/CustomFieldReferencesTile";
import { SharedContentTile } from "../../../common/sidebar/tiles/SharedContentTile";
import { GenericEntityLockButton } from "./GenericEntityLockButton";
import { Button } from "../../../common/buttons/Button/Button";
import { ToggleSwitch } from "../../../common/buttons/ToggleSwitch/ToggleSwitch";
import { GenericEntityUnlockButton } from "./GenericEntityUnlockButton";

export interface GenericEntityDetailPageProps<Entity extends GenericEntity> {
  detailTableProps?: GenericEntityDetailTableProps;
  hideCloneButton?: boolean;
  hideLockButton?: boolean;
  hideDeleteButton?: boolean;
  sidebarContent?: (entity: Entity) => ReactNode;
}

export const GenericEntityDetailPage = <
  Entity extends GenericEntity,
  ComponentProps extends GenericEntityDetailPageProps<Entity>
>({
  entityTypeId,
  entity,
  componentProps,
}: GenericEntityComponentProps<Entity, ComponentProps>) => {
  const suggestedEntityProps = useDefaultGenericEntityDetailPage({ entityTypeId, entity });
  const defaultComponentProps = useMemo(() => {
    return { ...suggestedEntityProps, ...componentProps };
  }, [componentProps, suggestedEntityProps]);

  const permissions = useEntityPermissions({ entityTypeId });
  const { canRead, canEdit } = permissions;
  if (!entity) return null;
  if (!canRead(entity)) return NotAllowed;

  const isSoftDeletable = Object.hasOwn(entity, "isDeleted");
  const isLockable = Object.hasOwn(entity, "lockedOn");

  const hasMoreActions =
    !defaultComponentProps?.hideCloneButton ||
    (isSoftDeletable && !defaultComponentProps.hideDeleteButton) ||
    (isLockable && !defaultComponentProps.hideLockButton);

  const hasSidebarContent = !!defaultComponentProps.sidebarContent;
  return (
    <EntityDetailView id={entity.id} entityTypeId={entityTypeId}>
      {(props) =>
        hasSidebarContent ? (
          <Detail
            head={
              <DetailsPageTopControlsView
                entityTypeId={entityTypeId}
                entity={entity}
                entityConstants={props.entityConstants}
                attributes={
                  <>
                    {isSoftDeletable && !!entity.isDeleted && (
                      <h2 style={{ margin: 0 }}>
                        <label
                          className="label label-sm label-soft-warning"
                          style={{ margin: 0 }}
                          title={`This ${props.entityConstants.entitySingular} was moved to trash.`}
                        >
                          <LucideIcon name="trash" />
                        </label>
                      </h2>
                    )}
                    {isLockable && !!entity.isLocked && (
                      <h2 style={{ margin: 0 }}>
                        <label
                          className="label label-sm label-soft-warning"
                          style={{ margin: 0 }}
                          title={`This ${props.entityConstants.entitySingular} has been locked.`}
                        >
                          <LucideIcon name="lock" />
                        </label>
                      </h2>
                    )}
                  </>
                }
                controls={
                  <>
                    <LinkButton
                      className="btn btn-default"
                      title={`Edit this ${props.entityConstants.entitySingular}`}
                      linkTo={props.routes.getEditLink(entity.id)}
                      disabled={!!entity.isDeleted || !canEdit(entity)}
                    >
                      <LucideIcon name="square-pen" /> Edit
                    </LinkButton>

                    {isSoftDeletable && !!entity.isDeleted && (
                      <EntityRestoreButton
                        entity={entity}
                        entityConstants={props.entityConstants}
                        entityApi={props.entityApi as any}
                        permissions={permissions}
                      />
                    )}

                    {hasMoreActions && (
                      <MoreDropdown drop="right" btn="btn btn-ghost-secondary">
                        <div className="flex align-center" style={{ flexDirection: "column", gap: "2.5px" }}>
                          {!defaultComponentProps?.hideCloneButton && (
                            <LinkButton
                              className="btn btn-sm btn-ghost-secondary btn-block"
                              style={{ width: "100%", textAlign: "left" }}
                              title={`Clone this ${props.entityConstants.entitySingular}`}
                              linkTo={props.routes.getCloneLink(entity.id)}
                              disabled={!permissions.canCreate}
                            >
                              <LucideIcon name="copy" /> Clone
                            </LinkButton>
                          )}
                          {isLockable && !defaultComponentProps.hideLockButton && (
                            <GenericEntityLockButton entityTypeId={entityTypeId} entity={entity} />
                          )}
                          {isLockable && !defaultComponentProps.hideLockButton && (
                            <GenericEntityUnlockButton entityTypeId={entityTypeId} entity={entity} />
                          )}
                          {isSoftDeletable && !defaultComponentProps.hideDeleteButton && (
                            <EntityDeleteButton entityTypeId={entityTypeId} entity={entity} />
                          )}
                        </div>
                      </MoreDropdown>
                    )}
                  </>
                }
              />
            }
            mainStyle={{ padding: "0px 10px" }}
            main={
              <div className="flex col-nowrap">
                <GenericEntityDetailTable
                  entityTypeId={entityTypeId}
                  entity={entity}
                  componentProps={defaultComponentProps?.detailTableProps}
                />
              </div>
            }
            sidebar={<>{defaultComponentProps.sidebarContent?.(entity)}</>}
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "5px",
              overflow: "hidden",
              height: "100%",
              width: "100%",
            }}
          >
            <DetailsPageTopControlsView
              entityTypeId={entityTypeId}
              entity={entity}
              entityConstants={props.entityConstants}
              attributes={
                <>
                  {isSoftDeletable && !!entity.isDeleted && (
                    <h2 style={{ margin: 0 }}>
                      <label
                        className="label label-sm label-soft-warning"
                        style={{ margin: 0 }}
                        title={`This ${props.entityConstants.entitySingular} was moved to trash.`}
                      >
                        <LucideIcon name="trash" />
                      </label>
                    </h2>
                  )}
                  {isLockable && !!entity.isLocked && (
                    <h2 style={{ margin: 0 }}>
                      <label
                        className="label label-sm label-soft-warning"
                        style={{ margin: 0 }}
                        title={`This ${props.entityConstants.entitySingular} is locked.`}
                      >
                        <LucideIcon name="lock" />
                      </label>
                    </h2>
                  )}
                </>
              }
              controls={
                <>
                  <LinkButton
                    className="btn btn-default"
                    title={`Edit this ${props.entityConstants.entitySingular}`}
                    linkTo={props.routes.getEditLink(entity.id)}
                    disabled={!!entity.isDeleted || !canEdit(entity)}
                  >
                    <LucideIcon name="square-pen" /> Edit
                  </LinkButton>

                  {isSoftDeletable && !!entity.isDeleted && (
                    <EntityRestoreButton
                      entity={entity}
                      entityConstants={props.entityConstants}
                      entityApi={props.entityApi as any}
                      permissions={permissions}
                    />
                  )}

                  {hasMoreActions && (
                    <MoreDropdown drop="right" btn="btn btn-ghost-secondary">
                      <div className="flex align-center" style={{ flexDirection: "column", gap: "2.5px" }}>
                        {!defaultComponentProps?.hideCloneButton && (
                          <LinkButton
                            className="btn btn-sm btn-ghost-secondary btn-block"
                            style={{ width: "100%", textAlign: "left" }}
                            title={`Clone this ${props.entityConstants.entitySingular}`}
                            linkTo={props.routes.getCloneLink(entity.id)}
                            disabled={!permissions.canCreate}
                          >
                            <LucideIcon name="copy" /> Clone
                          </LinkButton>
                        )}
                        {isLockable && !defaultComponentProps.hideLockButton && (
                          <GenericEntityLockButton entityTypeId={entityTypeId} entity={entity} />
                        )}
                        {isSoftDeletable && !defaultComponentProps.hideDeleteButton && (
                          <EntityDeleteButton entityTypeId={entityTypeId} entity={entity} />
                        )}
                      </div>
                    </MoreDropdown>
                  )}
                </>
              }
            />
            <div className="flex col-nowrap" style={{ overflow: "auto", height: "100%" }}>
              <GenericEntityDetailTable
                entityTypeId={entityTypeId}
                entity={entity}
                componentProps={defaultComponentProps?.detailTableProps}
              />
            </div>
          </div>
        )
      }
    </EntityDetailView>
  );
};

export const EntityDeleteButton = <Entity extends GenericEntity>({
  entity,
  entityTypeId,
  btnClassName,
  alwaysDeletePermanently = false,
  iconOnly = false,
}: {
  entity: Entity;
  entityTypeId: GenericEntityConstantsEntities;
  btnClassName?: (entity: Entity, shouldDeletePermanenlty: boolean) => string;
  alwaysDeletePermanently?: boolean;
  iconOnly?: boolean;
}) => {
  const { session } = useContext(SessionContext);
  const [showModal, setShowModal] = useState<boolean>(false);
  const { canDelete, canTrash } = useEntityPermissions({ entityTypeId });
  const { entityConstants } = genericEntityConstants[entityTypeId];
  const entityApi = useEntityApi(entityConstants.resource);
  const history = useHistory();
  const [deletePermanentlyControl, setDeletePermanentlyControl] = useState<boolean>(alwaysDeletePermanently);
  const deletePermanently = !!entity.isDeleted || deletePermanentlyControl;
  const hasPermission = deletePermanently ? canDelete(entity) : canTrash(entity);
  return (
    <>
      <Button
        className={
          btnClassName?.(entity, deletePermanently) ??
          `btn btn-sm btn-block btn-ghost-${deletePermanently ? "danger" : "warning"}`
        }
        style={{ textAlign: "left", margin: 0 }}
        title={
          !hasPermission
            ? `Insufficient permissions to ${deletePermanently ? "delete" : "trash"} the selected ${
                entityConstants.entityPlural
              }`
            : `${deletePermanently ? "Delete" : "Move to trash"}`
        }
        onClick={() => setShowModal(true)}
        disabled={!hasPermission}
      >
        <LucideIcon name="trash-2" /> {!iconOnly && `${deletePermanently ? "Delete" : "Trash"}`}
      </Button>

      <AlertModal
        type={`${deletePermanently ? "danger" : "warning"}`}
        showModal={showModal}
        setShowModal={setShowModal}
        title={`${deletePermanently ? `Permanently delete ${entityConstants.entitySingular}?` : "Move to trash?"}`}
        description={
          <div>
            {deletePermanently
              ? `This ${entityConstants.entitySingular} may be referenced by other entities. Deleting a ${entityConstants.entitySingular} is only possible if all references are removed.`
              : `This will move the ${entityConstants.entitySingular} into trash. It is possible to restore this ${entityConstants.entitySingular} at any time.`}

            {!!session?.features.development_mode && !alwaysDeletePermanently && (
              <>
                <hr style={{ margin: "10px 0", borderColor: "var(--gray-300)" }} />
                <label
                  className="flex align-center gap-5"
                  onClick={() => setDeletePermanentlyControl((prevState) => !prevState)}
                >
                  <ToggleSwitch checked={deletePermanently} /> Delete permanently
                </label>
              </>
            )}
          </div>
        }
        proceedLabel={deletePermanently ? "Delete" : "Trash"}
        onProceed={async () => {
          if (!hasPermission) return;
          if (deletePermanently) {
            await entityApi
              .deleteMutationAsync(
                {
                  id: entity.id,
                  goBackOnSuccess: false,
                  showToast: false,
                  entityName: entityConstants.entitySingular,
                  params: { deletePermanently: true },
                },

                {
                  onSuccess: () => {
                    showtoast("success", `Deleted ${toUppercase(entityConstants.entitySingular)}-ID: ${entity.id}.`);
                    history.goBack();
                  },
                }
              )
              .catch((e) => {});
          } else {
            await entityApi
              .deleteMutationAsync(
                {
                  id: entity.id,
                  goBackOnSuccess: false,
                  showToast: false,
                  entityName: entityConstants.entitySingular,
                },

                {
                  onSuccess: () => {
                    showtoast(
                      "success",
                      `Moved ${toUppercase(entityConstants.entitySingular)}-ID: ${entity.id} to trash.`
                    );
                    setShowModal(false);
                  },
                }
              )
              .catch((e) => {});
          }
        }}
        loading={entityApi.isLoadingDeleteMutation}
      />
    </>
  );
};

const useDefaultGenericEntityDetailPage: ({
  entityTypeId,
  entity,
}: {
  entityTypeId: GenericEntityConstantsEntities;
  entity?: GenericEntity;
}) => Partial<GenericEntityDetailPageProps<GenericEntity>> = ({ entityTypeId, entity }) => {
  const { session } = useContext(SessionContext);
  if (!entity) return {};
  switch (entityTypeId) {
    case "datasets":
      return {
        sidebarContent: (dataset: any) => (
          <>
            {!!dataset.relations?.sharedContent.count && (
              <SharedContentTile
                defaultFilters={{ datasetIds: [entity.id] }}
                excludeFilters={{ datasetIds: () => false }}
              />
            )}
            <CustomFieldReferencesTile
              entityType="Dataset"
              entity={entity}
              labNotebookEntryFilters={session?.features.eln ? { datasetReferenceIds: [entity.id] } : undefined}
            />
            {!dataset.relations?.sharedContent.count && <SharedContentTile disabled />}
          </>
        ),
      };
    case "samples":
      return {
        sidebarContent: () => (
          <>
            <CustomFieldReferencesTile
              entityType="Sample"
              entity={entity}
              labNotebookEntryFilters={session?.features.eln ? { sampleReferenceIds: [entity.id] } : undefined}
            />
          </>
        ),
      };
    default:
      return {};
  }
};
